import {
    $customization,
    $customizationExteriorColor,
    $customizationFeatures,
    $customizationInteriorColor,
    $customizationView
} from '../stores/customization';

export class Customization extends HTMLElement {
    private exteriorViewBtn: HTMLButtonElement;
    private interiorViewBtn: HTMLButtonElement;
    private exteriorColorBtns: NodeListOf<HTMLInputElement>;
    private interiorColorBtns: NodeListOf<HTMLInputElement>;
    private customizationFeatures: NodeListOf<HTMLLIElement>;
    private customizationImage: HTMLImageElement;
    private customizationData: any;
    private submitBtn: HTMLButtonElement;
    private customizationImagesTogglerIndicator: HTMLElement;
    // Listeners
    private unbindCustomizationViewListener: (() => void) | null = null;
    private unbindCustomizationExteriorColorListener: (() => void) | null = null;
    private unbindCustomizationInteriorColorListener: (() => void) | null = null;
    private unbindCustomizationFeaturesListener: (() => void) | null = null;
    private unbindCustomizationListener: (() => void) | null = null;
    constructor() {
        super();

        this.exteriorViewBtn = this.querySelector(
            '[data-customization-view="exterior"]'
        ) as HTMLButtonElement;
        this.interiorViewBtn = this.querySelector(
            '[data-customization-view="interior"]'
        ) as HTMLButtonElement;
        this.exteriorColorBtns = this.querySelectorAll(
            'input[data-customization-color-exterior]'
        ) as NodeListOf<HTMLInputElement>;
        this.interiorColorBtns = this.querySelectorAll(
            'input[data-customization-color-interior]'
        ) as NodeListOf<HTMLInputElement>;
        this.customizationFeatures = this.querySelectorAll(
            '[data-customization-feature]'
        ) as NodeListOf<HTMLLIElement>;
        this.customizationImage = this.querySelector('[data-customization-image]')?.querySelector(
            'img'
        ) as HTMLImageElement;
        this.customizationData = JSON.parse(this.getAttribute('data-customization') || '{}');
        this.submitBtn = this.querySelector(
            'button[data-customization-submit]'
        ) as HTMLButtonElement;
        this.customizationImagesTogglerIndicator = this.querySelector(
            '.c-purchase-experience_images_toggler_indicator'
        ) as HTMLElement;
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    connectedCallback() {
        //If no customization data is found, initialize the colors and features from the data, otherwise use the stored data
        if (
            $customization.get().product_id === '' ||
            $customization.get().product_id !== this.customizationData.id
        ) {
            $customization.setKey('colors', {
                exterior: this.customizationData.colors.exterior.find(
                    (color: any) => color.selected
                )?.id,
                interior: this.customizationData.colors.interior.find(
                    (color: any) => color.selected
                )?.id
            });
            $customization.setKey(
                'features',
                this.customizationData.features
                    .filter((feature: any) => feature.selected)
                    .map((feature: any) => feature.id)
            );
            $customization.setKey('product_id', this.customizationData.id);
        }
        this.bindEvents();
    }

    disconnectedCallback() {
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    private bindEvents(): void {
        this.unbindCustomizationExteriorColorListener = $customizationExteriorColor.subscribe(
            () => {
                this.onColorChange('exterior');
                this.onViewChange('exterior');
            }
        );
        this.unbindCustomizationInteriorColorListener = $customizationInteriorColor.subscribe(
            () => {
                this.onColorChange('interior');
                this.onViewChange('interior');
            }
        );
        this.unbindCustomizationFeaturesListener = $customizationFeatures.subscribe(
            this.onFeatureChange
        );
        this.unbindCustomizationViewListener = $customizationView.subscribe(this.onViewChange);
        this.unbindCustomizationListener = $customization.subscribe(this.onCustomizationChange);

        this.exteriorViewBtn.addEventListener('click', () => this.setView('exterior'));
        this.interiorViewBtn.addEventListener('click', () => this.setView('interior'));
        this.exteriorColorBtns.forEach((btn) =>
            btn.addEventListener('click', () => this.setColor('exterior', btn.id))
        );
        this.interiorColorBtns.forEach((btn) =>
            btn.addEventListener('click', () => this.setColor('interior', btn.id))
        );
        this.customizationFeatures.forEach((feature) =>
            feature
                .querySelector('button')
                ?.addEventListener('click', () => this.setFeature(feature.id))
        );
        this.submitBtn.addEventListener('click', () => this.onSubmit());
    }

    private unbindEvents(): void {
        this.unbindCustomizationViewListener = null;
        this.unbindCustomizationExteriorColorListener = null;
        this.unbindCustomizationInteriorColorListener = null;
        this.exteriorViewBtn.removeEventListener('click', () => this.setView('exterior'));
        this.interiorViewBtn.removeEventListener('click', () => this.setView('interior'));
        this.exteriorColorBtns.forEach((btn) =>
            btn.removeEventListener('click', () => this.setColor('exterior', btn.id))
        );
        this.interiorColorBtns.forEach((btn) =>
            btn.removeEventListener('click', () => this.setColor('interior', btn.id))
        );
        this.submitBtn.removeEventListener('click', () => this.onSubmit());
    }

    // =============================================================================
    // Callbacks
    // =============================================================================

    // Update the URL with the current customization data
    private onCustomizationChange = (): void => {
        const customization = $customization.get();
        const params = new URLSearchParams(window.location.search);

        // Set or update the color params
        if (customization.colors?.interior) {
            params.set('interior_color', customization.colors.interior);
        }
        if (customization.colors?.exterior) {
            params.set('exterior_color', customization.colors.exterior);
        }

        // Set or update the addons param (multiple params for multiple addons)
        if (Array.isArray(customization.features) && customization.features.length > 0) {
            params.delete('addons'); // Remove any existing 'addons' param
            customization.features.forEach((feature: string) => {
                params.append('addons', feature);
            });
        } else {
            params.delete('addons');
        }

        // Update the URL without reloading the page
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(window.history.state, '', newUrl);
    };

    // Update the image src based on the current view
    private onViewChange = (view: 'exterior' | 'interior'): void => {
        let src = '';
        if (view === 'exterior') {
            src = this.customizationData.colors[view].find(
                (color: any) => color.id === $customization.get().colors.exterior
            )?.image.src;
        } else if (view === 'interior') {
            src = this.customizationData.colors[view].find(
                (color: any) => color.id === $customization.get().colors.interior
            )?.image.src;
        }
        this.customizationImage.src = src;
        this.setViewButton(view);
    };

    // Update the feature buttons based on the current features
    private onFeatureChange = (): void => {
        this.customizationFeatures.forEach((feature) => {
            const btn = feature.querySelector('button');
            if (btn) {
                if ($customization.get().features.includes(feature.id)) {
                    btn.classList.add('-color-black');
                    const label = btn.querySelector('.c-button_label');
                    if (label) label.innerHTML = 'Remove';
                } else {
                    btn.classList.remove('-color-black');
                    btn.classList.add('-color-white');
                    const label = btn.querySelector('.c-button_label');
                    if (label) label.innerHTML = 'Add';
                }
            }
        });
    };

    private onColorChange = (view: 'exterior' | 'interior'): void => {
        const selectedColorId = $customization.get().colors[view];
        const btns = view === 'exterior' ? this.exteriorColorBtns : this.interiorColorBtns;
        btns.forEach((btn) => {
            btn.checked = btn.id === selectedColorId;
        });
    };

    // =============================================================================
    // Methods
    // =============================================================================

    private setView(view: 'exterior' | 'interior'): void {
        $customization.setKey('view', view);
        this.setViewButton(view);
    }

    private setViewButton(view: 'exterior' | 'interior'): void {
        if (view === 'exterior') {
            this.exteriorViewBtn.classList.add('-active');
            this.interiorViewBtn.classList.remove('-active');
            this.customizationImagesTogglerIndicator.classList.add('-left');
            this.customizationImagesTogglerIndicator.classList.remove('-right');
        } else {
            this.exteriorViewBtn.classList.remove('-active');
            this.interiorViewBtn.classList.add('-active');
            this.customizationImagesTogglerIndicator.classList.add('-right');
            this.customizationImagesTogglerIndicator.classList.remove('-left');
        }
    }

    private setColor(view: 'exterior' | 'interior', id: string): void {
        $customization.setKey('colors', {
            ...$customization.get().colors,
            [view]: id
        });
    }

    private setFeature(id: string): void {
        const features = $customization.get().features;
        if (features.includes(id)) {
            $customization.setKey(
                'features',
                features.filter((feature) => feature !== id)
            );
        } else {
            $customization.setKey('features', [...features, id]);
        }
    }

    private onSubmit = async (): Promise<void> => {
        await fetch('/review', {
            method: 'POST',
            body: JSON.stringify($customization.get())
        });
    };
}
